import React, { Component } from 'react';
import { Alert, Button, Intent } from '@blueprintjs/core';
import { observer, inject } from 'mobx-react';
import { observable } from 'mobx';
import Toast from 'helpers/toaster';
import { SettingsCard } from 'components/SettingsPage';
import { CurrentEnv } from '../../services/Api';

// What's linked to QuickBooks: each list carries its counts on its first record.
export const SYNCED_MODULES = [
  {
    key: 'taxes',
    label: 'Taxes',
    href: '/taxes',
    counts: (stores) => {
      const first = stores.taxStore.taxes.objects && stores.taxStore.taxes.objects[0];
      const counts = first && first.attributes.tax_counts;
      return counts && { linked: counts.link_tax_count, total: counts.total_tax_count };
    },
  },
  {
    key: 'products',
    label: 'Products and services',
    href: '/products_services',
    counts: (stores) => {
      const first = stores.productServiceStore.productsServices.objects && stores.productServiceStore.productsServices.objects[0];
      const counts = first && first.attributes.product_service_counts;
      return counts && { linked: counts.link_product_count, total: counts.total_product_count };
    },
  },
  {
    key: 'payment_methods',
    label: 'Payment methods',
    href: '/payment_methods',
    counts: (stores) => {
      const methods = stores.paymentMethodStore.paymentMethods;
      const first = methods && methods.objects && methods.objects[0];
      const counts = first && first.attributes.payment_method_counts;
      return counts && { linked: counts.link_payment_method_count, total: counts.total_payment_method_count };
    },
  },
];

// Settings > Accounting setup (pages/preferences/accounting_setup.js): the
// QuickBooks Online connection, and how much of each list is linked to it.
@inject('viewStore', 'userStore', 'quickbooksStore', 'taxStore', 'productServiceStore', 'paymentMethodStore') @observer
class QuickbooksCard extends Component {
  @observable isConfirmingDisconnect = false;

  redirectToAuth = () => {
    const { tenant_id, api_token } = this.props.viewStore.currentUser.attributes;
    window.location.href = `${CurrentEnv.domain()}/auth/quickbooks_redirect/?tenant_id=${tenant_id}&api_token=${api_token}`;
  };

  disconnect = () => {
    const { quickbooksStore, userStore } = this.props;
    this.isConfirmingDisconnect = false;
    quickbooksStore.disconnectFromQuickbooks()
      .then(() => {
        userStore.loadQuickbooks();
        Toast.show({ message: 'QuickBooks disconnected.', intent: Intent.SUCCESS });
      })
      .catch((error) => {
        const reason = error && error.response && error.response.data && error.response.data.errors;
        Toast.show({ message: reason || "Couldn't disconnect QuickBooks. Try again.", intent: Intent.DANGER });
      });
  };

  renderConnection() {
    const { userStore: { quickbooks: { data, isError } }, quickbooksStore } = this.props;
    const connected = Boolean(data && data.company_name);
    const address = connected && data.company_address
      ? [data.company_address.line1, data.company_address.city, data.company_address.postal_code].filter(Boolean).join(', ')
      : '';
    let status = <span className="sp-pill">Not connected</span>;
    if (connected) status = <span className="sp-pill sp-pill--green">Connected</span>;
    else if (isError) status = <span className="sp-pill sp-pill--red">Connection lost</span>;
    return (
      <SettingsCard className="acc-connection">
        <div className="acc-app">
          <span className="acc-app__logo"><img src="/quickbooks_logo_small.png" alt="" /></span>
          <div className="acc-app__text">
            <h2 className="acc-app__name">QuickBooks Online</h2>
            <p className="acc-app__hint">
              {connected
                ? 'You can send invoices to your QuickBooks account.'
                : 'Connect it to send invoices to your QuickBooks account.'}
            </p>
          </div>
          <div className="acc-app__status">{status}</div>
        </div>

        {connected && (
          <dl className="acc-details">
            <dt>Company</dt><dd>{data.company_name}</dd>
            {data.legal_name && (<><dt>Legal name</dt><dd>{data.legal_name}</dd></>)}
            {address && (<><dt>Address</dt><dd>{address}</dd></>)}
            {data.email && data.email.address && (<><dt>Email</dt><dd>{data.email.address}</dd></>)}
          </dl>
        )}

        {!connected && isError && (
          <p className="acc-lost" role="alert">
            QuickBooks is no longer connected, so invoices can't be sent to it. Connect it again below.
          </p>
        )}

        <div className="acc-actions">
          {connected ? (
            <Button
              className="sp-button sp-button--danger"
              icon="disable"
              text="Disconnect"
              loading={quickbooksStore.showQuickbooksConnectionLoader}
              onClick={() => { this.isConfirmingDisconnect = true; }}
            />
          ) : (
            <button type="button" className="acc-connect" onClick={this.redirectToAuth}>
              <img src="/quickbooks_connect.png" alt="Connect to QuickBooks" />
            </button>
          )}
        </div>
      </SettingsCard>
    );
  }

  renderSynced() {
    const rows = SYNCED_MODULES
      .map((module) => ({ ...module, counts: module.counts(this.props) }))
      .filter((module) => module.counts);
    if (rows.length === 0) return null;
    return (
      <SettingsCard title="Linked to QuickBooks" description="How many of each are linked to their QuickBooks match.">
        <ul className="acc-modules">
          {rows.map(({ key, label, href, counts }) => {
            const all = counts.total > 0 && counts.linked >= counts.total;
            const percent = counts.total > 0 ? Math.round((counts.linked / counts.total) * 100) : 0;
            return (
              <li key={key} className="acc-module">
                <span className="acc-module__label">{label}</span>
                <span className="acc-module__bar" aria-hidden="true">
                  <span className={`acc-module__fill${all ? ' is-done' : ''}`} style={{ width: `${percent}%` }} />
                </span>
                <span className="acc-module__count">{`${counts.linked} of ${counts.total} linked`}</span>
                {all
                  ? <span className="sp-pill sp-pill--green">All linked</span>
                  : <a className="acc-module__link" href={href} aria-label={`Link the rest of the ${label.toLowerCase()}`}>Link the rest</a>}
              </li>
            );
          })}
        </ul>
      </SettingsCard>
    );
  }

  render() {
    const connected = Boolean(this.props.userStore.quickbooks.data && this.props.userStore.quickbooks.data.company_name);
    return (
      <React.Fragment>
        {this.renderConnection()}
        {connected && this.renderSynced()}
        <Alert
          isOpen={this.isConfirmingDisconnect}
          icon="disable"
          intent={Intent.DANGER}
          cancelButtonText="Cancel"
          confirmButtonText="Disconnect QuickBooks"
          onCancel={() => { this.isConfirmingDisconnect = false; }}
          onConfirm={this.disconnect}
        >
          <strong>Disconnect QuickBooks?</strong>
          <p>Invoices won't be sent to QuickBooks until you connect it again.</p>
        </Alert>
      </React.Fragment>
    );
  }
}

export default QuickbooksCard;
